import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Plus, Search, Edit2, Trash2, X, DollarSign, Archive, Box } from 'react-feather';
import { base_api_url } from '../../../environment.jsx';
import './Depenses.css';

/* ── Constantes ── */
const TYPES = [
    { label: 'Stocké', value: 'stocke' },
    { label: 'Non stocké', value: 'non_stocke' },
];
const UNITES = ['kg', 'L', 'Pièce', 'Carton', 'Pack'];

/* Correspondance unités dépenses → unités ingrédients */
const MAP_UNITE = {
    'kg': 'kg',
    'L': 'L',
    'Pièce': 'pièce',
    'Carton': 'pièce',
    'Pack': 'pièce',
};

/* Générer un code unique à partir d'un libellé */
const genCode = (libelle) => {
    const prefix = libelle.replace(/\s+/g, '').toUpperCase().substring(0, 4);
    const suffix = String(Date.now()).slice(-5);
    return `${prefix}-${suffix}`;
};

const fmt = (n) =>
    new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n) + ' €';

const fmtDate = (d) => {
    if (!d) return '—';
    const [y, m, day] = d.split('-');
    return `${day}/${m}/${y}`;
};

/* ────────────────────────────────────────
   Modal Nouvel achat
───────────────────────────────────────── */
const ModalAchat = ({ onClose, onSave, loading, fournisseurs }) => {
    const [form, setForm] = useState({
        libelle_achat: '',
        type_depense: 'stocke',
        id_fournisseur: '',
        siren: '',
        quantite: 1,
        unite: 'kg',
        montant: '',
        tva: '20',
        date: '',
    });

    const handle = (e) => {
        const { name, value } = e.target;
        setForm((p) => ({ ...p, [name]: value }));
    };

    /* Auto-remplissage du SIREN quand fournisseur change */
    const handleFournisseurChange = (e) => {
        const idF = Number(e.target.value);
        const found = fournisseurs.find((f) => f.id_fournisseur === idF);
        setForm((p) => ({
            ...p,
            id_fournisseur: idF,
            siren: found?.siren || '',
        }));
    };

    const handleSubmit = () => {
        if (!form.libelle_achat.trim()) {
            Swal.fire({ icon: 'warning', text: 'Le libellé est requis.', toast: true, position: 'bottom-end', showConfirmButton: false, timer: 3000, customClass: { popup: 'pixel-toast' } });
            return;
        }
        if (!form.montant) {
            Swal.fire({ icon: 'warning', text: 'Le montant est requis.', toast: true, position: 'bottom-end', showConfirmButton: false, timer: 3000, customClass: { popup: 'pixel-toast' } });
            return;
        }
        onSave(form);
    };

    return (
        <>
            <div className="dep-backdrop" onClick={onClose} />
            <div className="dep-modal">
                {/* Header */}
                <div className="dep-modal-header">
                    <h5>Nouvel achat</h5>
                    <button className="dep-modal-close" onClick={onClose} type="button"><X size={18} /></button>
                </div>

                {/* Body */}
                <div className="dep-modal-body">
                    {/* Libellé */}
                    <div className="dep-field dep-field--full">
                        <label>LIBELLÉ DE L&apos;ACHAT <span className="dep-req">*</span></label>
                        <input name="libelle_achat" value={form.libelle_achat} onChange={handle} className="dep-input" placeholder="Ex: Achat Farine" />
                    </div>

                    {/* Type + Fournisseur */}
                    <div className="dep-field-row">
                        <div className="dep-field">
                            <label>TYPE <span className="dep-req">*</span></label>
                            <select name="type_depense" value={form.type_depense} onChange={handle} className="dep-select">
                                {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                        </div>
                        <div className="dep-field">
                            <label>FOURNISSEUR <span className="dep-req">*</span></label>
                            <select name="id_fournisseur" value={form.id_fournisseur} onChange={handleFournisseurChange} className="dep-select">
                                <option value="">— Sélectionner —</option>
                                {fournisseurs.map((f) => (
                                    <option key={f.id_fournisseur} value={f.id_fournisseur}>
                                        {f.nom_fournisseur}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* SIREN auto */}
                    <div className="dep-field dep-field--full">
                        <label>N° SIREN FOURNISSEUR</label>
                        <input name="siren" value={form.siren} readOnly className="dep-input dep-input--readonly" placeholder="Ex: 123 456 789" />
                    </div>

                    {/* Quantité + Unité */}
                    <div className="dep-field-row">
                        <div className="dep-field">
                            <label>QUANTITÉ</label>
                            <input name="quantite" value={form.quantite} onChange={handle} className="dep-input" type="number" min="1" />
                        </div>
                        <div className="dep-field">
                            <label>UNITÉ</label>
                            <select name="unite" value={form.unite} onChange={handle} className="dep-select">
                                {UNITES.map((u) => <option key={u}>{u}</option>)}
                            </select>
                        </div>
                    </div>

                    {/* Montant + TVA + Date */}
                    <div className="dep-field-row dep-field-row--3">
                        <div className="dep-field">
                            <label>MONTANT (€) <span className="dep-req">*</span></label>
                            <input name="montant" value={form.montant} onChange={handle} className="dep-input" type="number" min="0" step="0.01" placeholder="0.00" />
                        </div>
                        <div className="dep-field">
                            <label>TVA (%)</label>
                            <input name="tva" value={form.tva} onChange={handle} className="dep-input" type="number" placeholder="20" />
                        </div>
                        <div className="dep-field">
                            <label>DATE <span className="dep-req">*</span></label>
                            <input name="date" value={form.date} onChange={handle} className="dep-input" type="date" />
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="dep-modal-footer">
                    <button className="dep-btn-primary" onClick={handleSubmit} disabled={loading} type="button">
                        {loading ? 'Ajout en cours…' : 'Ajouter'}
                    </button>
                    <button className="dep-btn-secondary" onClick={onClose} type="button">Annuler</button>
                </div>
            </div>
        </>
    );
};

ModalAchat.propTypes = {
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    fournisseurs: PropTypes.array.isRequired,
};

/* ────────────────────────────────────────
   Modal Modification dépense
───────────────────────────────────────── */
const ModalEditAchat = ({ onClose, onSave, initial, loading }) => {
    const [form, setForm] = useState({
        libelle_achat: initial?.libelle_achat || '',
        type_depense: initial?.type_depense || 'stocke',
        quantite: initial?.quantite ?? 1,
        unite: initial?.unite || 'kg',
        montant: initial?.montant || '',
        tva: initial?.tva ?? '20',
        statut: initial?.statut ?? 1,
    });

    const handle = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

    const handleSubmit = () => {
        if (!form.libelle_achat.trim() || !form.montant) {
            Swal.fire({ icon: 'warning', text: 'Libellé et montant requis.', toast: true, position: 'bottom-end', showConfirmButton: false, timer: 3000, customClass: { popup: 'pixel-toast' } });
            return;
        }
        onSave({ ...form, statut: Number(form.statut) });
    };

    return (
        <>
            <div className="dep-backdrop" onClick={onClose} />
            <div className="dep-modal">
                <div className="dep-modal-header">
                    <h5>Modifier l&apos;achat</h5>
                    <button className="dep-modal-close" onClick={onClose} type="button"><X size={18} /></button>
                </div>
                <div className="dep-modal-body">
                    <div className="dep-field dep-field--full">
                        <label>LIBELLÉ DE L&apos;ACHAT <span className="dep-req">*</span></label>
                        <input name="libelle_achat" value={form.libelle_achat} onChange={handle} className="dep-input" />
                    </div>
                    <div className="dep-field-row">
                        <div className="dep-field">
                            <label>TYPE <span className="dep-req">*</span></label>
                            <select name="type_depense" value={form.type_depense} onChange={handle} className="dep-select">
                                {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                        </div>
                        <div className="dep-field">
                            <label>STATUT</label>
                            <select name="statut" value={form.statut} onChange={handle} className="dep-select">
                                <option value={1}>Actif</option>
                                <option value={2}>Inactif</option>
                            </select>
                        </div>
                    </div>
                    <div className="dep-field-row">
                        <div className="dep-field">
                            <label>QUANTITÉ</label>
                            <input name="quantite" value={form.quantite} onChange={handle} className="dep-input" type="number" min="1" />
                        </div>
                        <div className="dep-field">
                            <label>UNITÉ</label>
                            <select name="unite" value={form.unite} onChange={handle} className="dep-select">
                                {UNITES.map((u) => <option key={u}>{u}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="dep-field-row dep-field-row--3">
                        <div className="dep-field">
                            <label>MONTANT (€) <span className="dep-req">*</span></label>
                            <input name="montant" value={form.montant} onChange={handle} className="dep-input" type="number" min="0" step="0.01" />
                        </div>
                        <div className="dep-field">
                            <label>TVA (%)</label>
                            <input name="tva" value={form.tva} onChange={handle} className="dep-input" type="number" />
                        </div>
                    </div>
                </div>
                <div className="dep-modal-footer">
                    <button className="dep-btn-primary" onClick={handleSubmit} disabled={loading} type="button">
                        {loading ? 'Enregistrement…' : 'Enregistrer'}
                    </button>
                    <button className="dep-btn-secondary" onClick={onClose} type="button">Annuler</button>
                </div>
            </div>
        </>
    );
};

ModalEditAchat.propTypes = {
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    initial: PropTypes.object,
    loading: PropTypes.bool,
};

/* ────────────────────────────────────────
   Page principale — Mes achats
───────────────────────────────────────── */
const Depenses = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const ideresto = user?.restoId;
    const token = user?.access_token;
    const headers = { Authorization: `Bearer ${token}` };

    const [liste, setListe] = useState([]);
    const [fournisseurs, setFournisseurs] = useState([]);
    const [chargement, setChargement] = useState(true);
    const [saving, setSaving] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [recherche, setRecherche] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editItem, setEditItem] = useState(null);

    /* ── Récupération fournisseurs ── */
    const fetchFournisseurs = useCallback(async () => {
        try {
            const res = await axios.get(`${base_api_url}depense/fournisseur/${ideresto}`, { headers });
            const data = Array.isArray(res.data?.fournisseur) ? res.data.fournisseur : [];
            setFournisseurs(data);
        } catch (err) {
            console.error('Fournisseurs:', err);
        }
    }, [ideresto]);

    /* ── Récupération achats ── */
    const fetchAchats = useCallback(async () => {
        setChargement(true);
        try {
            const res = await axios.get(`${base_api_url}depense/${ideresto}`, { headers });
            const data = Array.isArray(res.data?.depenses ?? res.data)
                ? (res.data?.depenses ?? res.data)
                : [];
            setListe(data);
        } catch (err) {
            console.error('Achats:', err);
            // API non disponible → liste vide, pas de toast bloquant
        } finally {
            setChargement(false);
        }
    }, [ideresto]);

    useEffect(() => {
        fetchFournisseurs();
        fetchAchats();
    }, [fetchFournisseurs, fetchAchats]);

    /* ── Ajout d'un achat ── */
    const handleSave = async (form) => {
        setSaving(true);
        try {
            const payload = {
                id_fournisseur: Number(form.id_fournisseur) || null,
                id_restaurant: ideresto,
                libelle_achat: form.libelle_achat,
                montant: Number(form.montant),
                quantite: Number(form.quantite),
                tva: String(form.tva),
                type_depense: form.type_depense,
                unite: form.unite,
            };
            const res = await axios.post(`${base_api_url}depense`, payload, { headers });
            Swal.fire({
                icon: 'success',
                text: res.data?.message || 'Achat ajouté avec succès.',
                toast: true, position: 'bottom-end', showConfirmButton: false, timer: 3000,
                customClass: { popup: 'pixel-toast' },
            });
            setShowModal(false);
            fetchAchats();

            /* ── Création automatique d'ingrédient si type non stocké ── */
            if (form.type_depense === 'non_stocke') {
                try {
                    const qty = Number(form.quantite) || 1;
                    const montant = Number(form.montant) || 0;
                    const ingredientPayload = {
                        nom: form.libelle_achat,
                        code: genCode(form.libelle_achat),
                        categorie: 'Autres',
                        unite: MAP_UNITE[form.unite] || 'pièce',
                        prix_unitaire: parseFloat((montant / qty).toFixed(4)),
                        quantite_initiale: qty,
                        seuil_alerte: 10,
                        id_restaurant: ideresto,
                    };
                    await axios.post(`${base_api_url}api/ingredient`, ingredientPayload, { headers });
                    Swal.fire({
                        icon: 'success',
                        text: `✓ Ingrédient « ${form.libelle_achat} » ajouté au stock automatiquement.`,
                        toast: true, position: 'bottom-end', showConfirmButton: false, timer: 4000,
                        customClass: { popup: 'pixel-toast' },
                    });
                } catch (ingredientErr) {
                    console.warn('Ingrédient auto-création échouée:', ingredientErr);
                    Swal.fire({
                        icon: 'warning',
                        text: `Dépense enregistrée, mais l'ajout automatique au stock a échoué. Vérifiez l'inventaire.`,
                        toast: true, position: 'bottom-end', showConfirmButton: false, timer: 5000,
                        customClass: { popup: 'pixel-toast' },
                    });
                }
            }
        } catch (err) {
            console.error('Ajout achat:', err);
            Swal.fire({
                icon: 'error',
                text: err.response?.data?.message || 'Impossible d\'ajouter l\'achat.',
                toast: true, position: 'bottom-end', showConfirmButton: false, timer: 4000,
                customClass: { popup: 'pixel-toast' },
            });
        } finally {
            setSaving(false);
        }
    };

    /* ── Mise à jour d'un achat ── */
    const handleUpdate = async (form) => {
        const id = editItem.id_depense ?? editItem.id;
        setUpdating(true);
        try {
            const payload = {
                libelle_achat: form.libelle_achat,
                montant: Number(form.montant),
                quantite: Number(form.quantite),
                statut: form.statut,
                tva: String(form.tva),
                type_depense: form.type_depense,
                unite: form.unite,
            };
            const res = await axios.put(`${base_api_url}depense/${id}`, payload, { headers });
            Swal.fire({
                icon: 'success',
                text: res.data?.message || 'Dépense mise à jour.',
                toast: true, position: 'bottom-end', showConfirmButton: false, timer: 3000,
                customClass: { popup: 'pixel-toast' },
            });
            setEditItem(null);
            fetchAchats();
        } catch (err) {
            console.error('Mise à jour dépense:', err);
            Swal.fire({
                icon: 'error',
                text: err.response?.data?.message || 'Impossible de mettre à jour.',
                toast: true, position: 'bottom-end', showConfirmButton: false, timer: 4000,
                customClass: { popup: 'pixel-toast' },
            });
        } finally {
            setUpdating(false);
        }
    };

    /* ── Suppression ── */
    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Supprimer cet achat ?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            confirmButtonText: 'Supprimer',
            cancelButtonText: 'Annuler',
        });
        if (!result.isConfirmed) return;
        try {
            await axios.put(`${base_api_url}depense/${id}`, { statut: 0 }, { headers });
            setListe((prev) => prev.filter((d) => (d.id_depense ?? d.id) !== id));
            Swal.fire({ icon: 'success', text: 'Achat supprimé.', toast: true, position: 'bottom-end', showConfirmButton: false, timer: 2500, customClass: { popup: 'pixel-toast' } });
        } catch (err) {
            console.error('Suppression:', err);
            Swal.fire({ icon: 'error', text: 'Impossible de supprimer.', toast: true, position: 'bottom-end', showConfirmButton: false, timer: 3500, customClass: { popup: 'pixel-toast' } });
        }
    };

    /* ── Calculs ── */
    const filtered = liste.filter(
        (d) =>
            (d.libelle_achat || '').toLowerCase().includes(recherche.toLowerCase()) ||
            (d.type_depense || '').toLowerCase().includes(recherche.toLowerCase())
    );
    const total = liste.reduce((s, d) => s + Number(d.montant || 0), 0);
    const stocke = liste.filter((d) => d.type_depense === 'stocke').reduce((s, d) => s + Number(d.montant || 0), 0);
    const nonStocke = total - stocke;

    /* ── Helpers affichage ── */
    const nomFournisseur = (id) => fournisseurs.find((f) => f.id_fournisseur === id)?.nom_fournisseur || '—';
    const typeLabel = (t) => t === 'stocke' ? 'STOCKÉ' : 'NON STOCKÉ';
    const typeCls = (t) => t === 'stocke' ? 'dep-stocke' : 'dep-non-stocke';

    return (
        <div className="page-wrapper">
            <div className="dep-page">
                {/* ── En-tête ── */}
                <div className="dep-header">
                    <div>
                        <h2 className="dep-title">Mes achats</h2>
                        <p className="dep-subtitle">Ensemble des flux financiers et factures.</p>
                    </div>
                    <button className="dep-btn-add" onClick={() => setShowModal(true)}>
                        <Plus size={16} className="d-md-none" />
                        <span className="d-none d-md-inline">Nouvelle dépense</span>
                    </button>
                </div>

                {/* ── Recherche ── */}
                <div className="dep-search-wrap">
                    <Search size={15} className="dep-search-icon" />
                    <input
                        className="dep-search"
                        placeholder="Rechercher une dépense..."
                        value={recherche}
                        onChange={(e) => setRecherche(e.target.value)}
                    />
                </div>

                {/* ── Stats ── */}
                <div className="dep-stats">
                    <div className="dep-stat-card">
                        <div className="dep-stat-info">
                            <span className="dep-stat-label">TOTAL GÉNÉRAL</span>
                            <span className="dep-stat-value">{fmt(total)}</span>
                        </div>
                        <div className="dep-stat-icon dep-stat-icon--orange"><DollarSign size={22} /></div>
                    </div>
                    <div className="dep-stat-card">
                        <div className="dep-stat-info">
                            <span className="dep-stat-label">DÉPENSES STOCKÉES</span>
                            <span className="dep-stat-value">{fmt(stocke)}</span>
                        </div>
                        <div className="dep-stat-icon dep-stat-icon--blue"><Archive size={22} /></div>
                    </div>
                    <div className="dep-stat-card">
                        <div className="dep-stat-info">
                            <span className="dep-stat-label">NON STOCKÉES</span>
                            <span className="dep-stat-value">{fmt(nonStocke)}</span>
                        </div>
                        <div className="dep-stat-icon dep-stat-icon--purple"><Box size={22} /></div>
                    </div>
                </div>

                {/* ── Tableau ── */}
                <div className="dep-table-wrap">
                    {chargement ? (
                        <div className="dep-loading">Chargement…</div>
                    ) : (
                        <>
                            {/* Table desktop */}
                            <table className="dep-table">
                                <thead>
                                    <tr>
                                        <th>DÉPENSE</th>
                                        <th>FOURNISSEUR</th>
                                        <th>TYPE</th>
                                        <th className="dep-th-right">MONTANT</th>
                                        <th>DATE</th>
                                        <th className="dep-th-actions">ACTIONS</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.length === 0 ? (
                                        <tr><td colSpan={6} className="dep-empty">Aucune dépense trouvée.</td></tr>
                                    ) : filtered.map((d) => {
                                        const id = d.id_depense ?? d.id;
                                        return (
                                            <tr key={id} className="dep-row">
                                                <td>
                                                    <span className="dep-nom">{d.libelle_achat}</span>
                                                    {d.unite && <span className="dep-cat">{d.unite}</span>}
                                                </td>
                                                <td className="dep-fournisseur">{nomFournisseur(d.id_fournisseur)}</td>
                                                <td>
                                                    <span className={`dep-type-badge ${typeCls(d.type_depense)}`}>
                                                        {typeLabel(d.type_depense)}
                                                    </span>
                                                </td>
                                                <td className="dep-montant">{fmt(d.montant)}</td>
                                                <td className="dep-date">{fmtDate(d.date)}</td>
                                                <td className="dep-actions-cell">
                                                    <div className="dep-row-actions">
                                                        <button className="dep-action-btn dep-edit" onClick={() => setEditItem(d)} title="Modifier"><Edit2 size={14} /></button>
                                                        <button className="dep-action-btn dep-delete" onClick={() => handleDelete(id)} title="Supprimer"><Trash2 size={14} /></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>

                            {/* Cards mobile */}
                            <div className="dep-cards-mobile">
                                {filtered.length === 0 ? (
                                    <p className="dep-empty">Aucune dépense trouvée.</p>
                                ) : filtered.map((d) => {
                                    const id = d.id_depense ?? d.id;
                                    return (
                                        <div key={id} className="dep-card-mobile">
                                            <div className="dep-card-top">
                                                <div>
                                                    <span className="dep-nom">{d.libelle_achat}</span>
                                                    {d.unite && <span className="dep-cat">{d.unite}</span>}
                                                </div>
                                                <span className="dep-montant-bold">{fmt(d.montant)}</span>
                                            </div>
                                            <div className="dep-card-bottom">
                                                <span className="dep-fournisseur">{nomFournisseur(d.id_fournisseur)}</span>
                                                <span className={`dep-type-badge ${typeCls(d.type_depense)}`}>{typeLabel(d.type_depense)}</span>
                                                <span className="dep-date">{fmtDate(d.date)}</span>
                                                <div className="dep-card-actions">
                                                    <button className="dep-action-btn dep-edit" onClick={() => setEditItem(d)}><Edit2 size={13} /></button>
                                                    <button className="dep-action-btn dep-delete" onClick={() => handleDelete(id)}><Trash2 size={13} /></button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>

                {/* ── Modal Ajout ── */}
                {showModal && (
                    <ModalAchat
                        onClose={() => setShowModal(false)}
                        onSave={handleSave}
                        loading={saving}
                        fournisseurs={fournisseurs}
                    />
                )}

                {/* ── Modal Modification ── */}
                {editItem && (
                    <ModalEditAchat
                        onClose={() => setEditItem(null)}
                        onSave={handleUpdate}
                        initial={editItem}
                        loading={updating}
                    />
                )}
            </div>
        </div>
    );
};

export default Depenses;
